// Whitelabel button colors (/branding-setup → company.button_bg_color /
// button_font_color, exposed to the admin and its agents as
// whitelabel_branding.*). They recolor every SOLID success and info button
// (.btn-success / .btn-info — not .btn-soft-* or .btn-outline-*), injected as
// a <style> block by AppMenu.
//
// Pilot scope: only the pages in BUTTON_THEME_PATHS for now. To apply it
// across the whole app, make isButtonThemePath() return true (or drop the
// check in AppMenu) — the CSS itself is already global.
const BUTTON_THEME_PATHS = [
  "/branding-setup",
  // Flight booking flow
  "/flight-booking",
  "/passenger-details",
  "/booking-details",
  // Hotel booking flow
  "/hotel-booking",
  "/select-room",
  "/guest-details",
  // Travel insurance flow
  "/travel-insurance",
  "/travel-insurance/checkout",
  // Whitelabel-only pages
  "/manage-booking",
];

const HEX_COLOR_RE = /^#[0-9A-Fa-f]{6}$/;

export const isButtonThemePath = (pathname: string): boolean =>
  BUTTON_THEME_PATHS.includes(pathname.replace(/\/+$/, "") || "/");

const SOLID_BUTTONS = [".btn.btn-success", ".btn.btn-info"];

const states = (suffixes: string[]) =>
  SOLID_BUTTONS.flatMap((b) => suffixes.map((s) => `${b}${s}`)).join(", ");

/**
 * The CSS for the whitelabel button colors, or null when neither color is
 * set (or isn't a valid #rrggbb hex).
 */
export const buildButtonThemeCss = (
  bgColor?: string | null,
  fontColor?: string | null
): string | null => {
  const bg = HEX_COLOR_RE.test(bgColor || "") ? bgColor : null;
  const fg = HEX_COLOR_RE.test(fontColor || "") ? fontColor : null;
  if (!bg && !fg) return null;

  const decl = [
    bg &&
      `background-color: ${bg} !important; border-color: ${bg} !important; --vz-btn-bg: ${bg}; --vz-btn-border-color: ${bg}; --vz-btn-hover-bg: ${bg}; --vz-btn-hover-border-color: ${bg}; --vz-btn-active-bg: ${bg}; --vz-btn-active-border-color: ${bg}; --vz-btn-disabled-bg: ${bg}; --vz-btn-disabled-border-color: ${bg};`,
    fg &&
      `color: ${fg} !important; --vz-btn-color: ${fg}; --vz-btn-hover-color: ${fg}; --vz-btn-active-color: ${fg}; --vz-btn-disabled-color: ${fg};`,
  ]
    .filter(Boolean)
    .join(" ");

  return [
    `${states(["", ":hover", ":focus", ":active", ".active", ":disabled", ".disabled"])} { ${decl} }`,
    // Icons/spinners inside the button follow the text color.
    fg && `${states([" i", " svg", " .spinner-border"])} { color: ${fg} !important; fill: ${fg} !important; }`,
    // A same-color hover would look dead — darken slightly instead.
    `${states([":hover", ":focus-visible"])} { filter: brightness(0.92); }`,
  ]
    .filter(Boolean)
    .join("\n");
};
